export type ApplicabilityClass =
  | "direct-law"
  | "contractual-requirement"
  | "voluntary-certification"
  | "accreditation"
  | "common-audit-expectation"
  | "industry-best-practice"
  | "optional-guidance";

export type FrameworkStatus =
  | "published-current"
  | "under-publication"
  | "under-revision"
  | "watchlist";

export type ProfessionalReviewState =
  | "official-source-verified"
  | "professional-review-required"
  | "professionally-reviewed";

export type FrameworkRegistryEntry = {
  frameworkId: string;
  frameworkName: string;
  issuer: string;
  jurisdiction: string;
  version: string;
  status: FrameworkStatus;
  publicationDate: string | null;
  effectiveDate: string | null;
  transitionPeriod: string;
  appliesTo: string;
  thresholds: string;
  applicability: readonly ApplicabilityClass[];
  procurementRelevance: string;
  evidenceCommonlyRequested: readonly string[];
  raqozSupport: readonly string[];
  missingCapability: readonly string[];
  professionalReview: ProfessionalReviewState;
  claimRisk: "low" | "medium" | "high";
  commercialUrgency: "now" | "validate-next" | "watch";
  searchDemand: "baseline-pending";
  channelOpportunity: string;
  primarySource: string;
  secondarySources: readonly string[];
  lastVerified: string;
  nextReview: string;
  dependentRoutes: readonly string[];
  dependentProductMappings: readonly string[];
  publicClaimAllowed: boolean;
  changeNotes: string;
};

export const frameworkRegistry: readonly FrameworkRegistryEntry[] = [
  {
    frameworkId: "iso-9001-2015-amd1-2024",
    frameworkName: "ISO 9001 quality management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 9001:2015 with Amendment 1:2024",
    status: "published-current",
    publicationDate: "2015-09",
    effectiveDate: null,
    transitionPeriod: "Replacement edition is scheduled for publication on 2026-09-16; transition guidance must be verified after publication.",
    appliesTo: "Organisations choosing to use or seek certification to the standard, subject to their certification and contractual context.",
    thresholds: "No RAQOZ eligibility conclusion. Certification and customer requirements are organisation-specific.",
    applicability: ["voluntary-certification", "contractual-requirement"],
    procurementRelevance: "Externally provided products, processes and services; supplier control; purchasing information; verification and performance evidence.",
    evidenceCommonlyRequested: ["Requirements", "Supplier evaluation", "Purchasing records", "Receipt or verification evidence", "Supplier performance and exceptions"],
    raqozSupport: ["Requirements and requisitions", "Supplier evaluation", "RFQ and award evidence", "Purchase orders", "Receipt and supplier-performance evidence"],
    missingCapability: ["Practitioner-reviewed control mapping", "Versioned ISO 9001 Control Pack"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "now",
    searchDemand: "baseline-pending",
    channelOpportunity: "ISO implementation consultants, quality managers and manufacturing SMEs",
    primarySource: "https://www.iso.org/standard/62085.html",
    secondarySources: ["https://www.iso.org/standard/88431.html"],
    lastVerified: "2026-09-01",
    nextReview: "2026-09-16",
    dependentRoutes: ["/iso-9001-procurement"],
    dependentProductMappings: ["supplier-evaluation", "purchase-order", "receipt", "supplier-performance"],
    publicClaimAllowed: true,
    changeNotes: "Use independent summaries only. Do not reproduce protected clause text or imply certification.",
  },
  {
    frameworkId: "iso-9001-2026",
    frameworkName: "ISO 9001 quality management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 9001:2026",
    status: "under-publication",
    publicationDate: "2026-09-16",
    effectiveDate: null,
    transitionPeriod: "Scheduled publication date only; confirm the final edition and transition guidance after release.",
    appliesTo: "Not yet treated as a published final edition on the last-verified date.",
    thresholds: "Not applicable until official publication and practitioner review.",
    applicability: ["voluntary-certification"],
    procurementRelevance: "Hold detailed mapping until the final licensed edition is reviewed.",
    evidenceCommonlyRequested: [],
    raqozSupport: [],
    missingCapability: ["Licensed final-edition review", "Qualified practitioner review", "Approved transition mapping"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "now",
    searchDemand: "baseline-pending",
    channelOpportunity: "ISO implementation consultants and quality managers",
    primarySource: "https://www.iso.org/standard/88464.html",
    secondarySources: ["https://committee.iso.org/sites/tc176sc2/home/news/content-left-area/news-and-updates/news-1.html"],
    lastVerified: "2026-09-01",
    nextReview: "2026-09-16",
    dependentRoutes: ["/iso-9001-procurement"],
    dependentProductMappings: [],
    publicClaimAllowed: false,
    changeNotes: "No clause comparison, transition claim or 2026 Control Pack before official publication and qualified review.",
  },
  {
    frameworkId: "in-msmed-act-2006",
    frameworkName: "Micro, Small and Medium Enterprises Development Act, 2006",
    issuer: "Government of India",
    jurisdiction: "India",
    version: "Act 27 of 2006, current text subject to official verification",
    status: "published-current",
    publicationDate: "2006-06-16",
    effectiveDate: "2006-10-02",
    transitionPeriod: "Not applicable.",
    appliesTo: "Transactions within the Act's scope involving eligible micro or small enterprise suppliers; eligibility requires factual and professional review.",
    thresholds: "RAQOZ does not determine enterprise eligibility, acceptance, due date, interest or forum jurisdiction.",
    applicability: ["direct-law"],
    procurementRelevance: "Supplier classification, agreed terms, acceptance or deemed acceptance, invoice and payment evidence.",
    evidenceCommonlyRequested: ["Supplier declaration", "Udyam evidence", "Contract terms", "Acceptance evidence", "Invoice and payment status"],
    raqozSupport: ["Supplier records", "Purchase order terms", "Receipt or service acceptance", "Invoice and payment-state evidence", "Operational tracker"],
    missingCapability: ["Verified eligibility service", "Statutory-interest determination"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "now",
    searchDemand: "baseline-pending",
    channelOpportunity: "Chartered accountants, finance teams and Indian SMEs",
    primarySource: "https://www.indiacode.nic.in/handle/123456789/12875",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: ["/msme-payment-controls-india", "/tools/msme-45-day-payment-tracker"],
    dependentProductMappings: ["supplier-master", "purchase-order", "receipt", "invoice", "payment-state"],
    publicClaimAllowed: true,
    changeNotes: "Operational evidence only; current legal review remains required.",
  },
  {
    frameworkId: "in-income-tax-43b-h",
    frameworkName: "Income-tax Act section 43B(h)",
    issuer: "Government of India",
    jurisdiction: "India",
    version: "Section 43B(h), reporting context last verified 2026-09-01",
    status: "published-current",
    publicationDate: "2023",
    effectiveDate: "2024-04-01",
    transitionPeriod: "Tax-year and assessment-year treatment require qualified review.",
    appliesTo: "Taxpayers and sums within the provision's scope; RAQOZ does not determine applicability or tax treatment.",
    thresholds: "Supplier classification, transaction facts and current law must be reviewed professionally.",
    applicability: ["direct-law", "common-audit-expectation"],
    procurementRelevance: "Supplier classification, acceptance, agreed terms, invoice, payment and period-end evidence.",
    evidenceCommonlyRequested: ["Supplier declaration", "Order terms", "Acceptance date", "Invoice", "Payment date", "Professional conclusion"],
    raqozSupport: ["Supplier records", "Order and receipt chronology", "Invoice and payment-state evidence"],
    missingCapability: ["Tax deduction or disallowance engine", "Professional conclusion workflow"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "now",
    searchDemand: "baseline-pending",
    channelOpportunity: "Chartered accountants and Indian finance teams",
    primarySource: "https://www.incometax.gov.in/iec/foportal/sites/default/files/2024-09/Webinar%20PPT%20for%203CA-3CD_3CB-3CD_13.09.2024.pdf",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: ["/section-43b-h-procurement"],
    dependentProductMappings: ["supplier-master", "purchase-order", "receipt", "invoice", "payment-state"],
    publicClaimAllowed: true,
    changeNotes: "No eligibility, due-date, interest, deduction or disallowance conclusion.",
  },
  {
    frameworkId: "iso-15189-2022",
    frameworkName: "Medical laboratories — requirements for quality and competence",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 15189:2022",
    status: "published-current",
    publicationDate: "2022-12",
    effectiveDate: null,
    transitionPeriod: "Accreditation-body transition rules are jurisdiction-specific.",
    appliesTo: "Medical laboratories using the standard or seeking accreditation against it.",
    thresholds: "No automatic applicability or accreditation conclusion.",
    applicability: ["accreditation", "contractual-requirement"],
    procurementRelevance: "External products and services, equipment, reagents, consumables, supplier evidence and acceptance records require specialist mapping.",
    evidenceCommonlyRequested: ["Approved supplier records", "Equipment and reagent records", "Receipt or acceptance", "Nonconformity evidence", "Supplier review"],
    raqozSupport: ["Supplier onboarding", "Equipment and consumable procurement", "Receipt evidence", "Warranty and maintenance records"],
    missingCapability: ["Laboratory practitioner-reviewed mapping", "Accreditation-specific evidence pack"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "validate-next",
    searchDemand: "baseline-pending",
    channelOpportunity: "Laboratory quality managers and permissioned design partners",
    primarySource: "https://www.iso.org/standard/76677.html",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: [],
    dependentProductMappings: ["supplier-master", "asset", "consumable", "receipt", "maintenance"],
    publicClaimAllowed: false,
    changeNotes: "No detailed public mapping before laboratory-quality review.",
  },
  {
    frameworkId: "iso-13485-2016",
    frameworkName: "Medical devices — quality management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 13485:2016",
    status: "published-current",
    publicationDate: "2016-03",
    effectiveDate: null,
    transitionPeriod: "Jurisdiction and certification arrangements vary.",
    appliesTo: "Medical-device organisations, suppliers and external parties using the standard within their regulatory and certification context.",
    thresholds: "No RAQOZ certification or regulatory-applicability conclusion.",
    applicability: ["voluntary-certification", "contractual-requirement"],
    procurementRelevance: "Supplier qualification, purchasing information, verification, traceability and change evidence require specialist review.",
    evidenceCommonlyRequested: ["Supplier qualification", "Purchasing requirements", "Verification records", "Change history", "Supplier performance"],
    raqozSupport: ["Supplier onboarding", "Requirements", "RFQ and award evidence", "Purchase orders", "Receipt and discrepancy evidence"],
    missingCapability: ["Qualified medical-device mapping", "Quality-agreement and regulated change-control support"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "validate-next",
    searchDemand: "baseline-pending",
    channelOpportunity: "Medical-device quality professionals",
    primarySource: "https://www.iso.org/standard/59752.html",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: [],
    dependentProductMappings: ["supplier-master", "purchase-order", "receipt", "supplier-performance"],
    publicClaimAllowed: false,
    changeNotes: "Specialist wedge only; no public detailed mapping before qualified review.",
  },
  {
    frameworkId: "us-fda-qmsr-2026",
    frameworkName: "FDA Quality Management System Regulation",
    issuer: "U.S. Food and Drug Administration",
    jurisdiction: "United States",
    version: "21 CFR Part 820 QMSR effective 2026-02-02",
    status: "published-current",
    publicationDate: "2024-02-02",
    effectiveDate: "2026-02-02",
    transitionPeriod: "Effective; FDA applicability and inspection scope require regulatory counsel or qualified expertise.",
    appliesTo: "Finished device manufacturers and other entities within the FDA rule's defined scope.",
    thresholds: "RAQOZ does not determine device, manufacturer or regulatory status.",
    applicability: ["direct-law"],
    procurementRelevance: "Purchasing and supplier evidence may be relevant, but QMSR is not a procurement-only regulation.",
    evidenceCommonlyRequested: ["Quality-system records", "Supplier controls", "Purchasing records", "Verification and change records"],
    raqozSupport: ["Procurement source records and supplier evidence"],
    missingCapability: ["Regulatory mapping", "Complete medical-device QMS functionality"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "validate-next",
    searchDemand: "baseline-pending",
    channelOpportunity: "Selected US medical-device manufacturers",
    primarySource: "https://www.fda.gov/medical-devices/postmarket-requirements-devices/quality-management-system-regulation-qmsr",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: [],
    dependentProductMappings: [],
    publicClaimAllowed: false,
    changeNotes: "Watch and validate; do not claim RAQOZ provides QMSR compliance.",
  },
  {
    frameworkId: "iso-27001-2022",
    frameworkName: "Information security management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO/IEC 27001:2022",
    status: "published-current",
    publicationDate: "2022-10",
    effectiveDate: null,
    transitionPeriod: "Certification transition depends on the organisation and certification body.",
    appliesTo: "Organisations choosing to operate or certify an ISMS.",
    thresholds: "No automatic applicability.",
    applicability: ["voluntary-certification", "contractual-requirement"],
    procurementRelevance: "Only supplier and externally provided ICT-service evidence genuinely connected to procurement.",
    evidenceCommonlyRequested: ["Supplier assessment", "Contractual requirements", "Review and monitoring evidence"],
    raqozSupport: ["Supplier onboarding", "Contract and purchasing evidence"],
    missingCapability: ["ISMS risk management", "Information-security control system"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "watch",
    searchDemand: "baseline-pending",
    channelOpportunity: "Security-conscious buyers; not an immediate acquisition wedge",
    primarySource: "https://www.iso.org/standard/27001",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2027-03-01",
    dependentRoutes: [],
    dependentProductMappings: ["supplier-master", "contract"],
    publicClaimAllowed: false,
    changeNotes: "RAQOZ is not a complete ISMS platform and does not claim ISO/IEC 27001 certification.",
  },
  {
    frameworkId: "iso-37001-2025",
    frameworkName: "Anti-bribery management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 37001:2025",
    status: "published-current",
    publicationDate: "2025-02",
    effectiveDate: null,
    transitionPeriod: "Certification arrangements require qualified review.",
    appliesTo: "Organisations choosing to use or certify an anti-bribery management system.",
    thresholds: "No automatic applicability.",
    applicability: ["voluntary-certification", "contractual-requirement"],
    procurementRelevance: "Selected supplier due-diligence, conflict, approval, single-source and decision-traceability evidence only.",
    evidenceCommonlyRequested: ["Supplier due diligence", "Conflict declarations", "Approval independence", "Exceptions", "Decision evidence"],
    raqozSupport: ["Supplier evidence", "Maker-checker approvals", "Award rationale", "Immutable events"],
    missingCapability: ["Anti-bribery programme", "Gifts and hospitality controls", "Practitioner-reviewed mapping"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "watch",
    searchDemand: "baseline-pending",
    channelOpportunity: "Procurement-integrity specialists after validation",
    primarySource: "https://www.iso.org/standard/37001",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2027-03-01",
    dependentRoutes: [],
    dependentProductMappings: ["supplier-master", "approval", "award", "audit-events"],
    publicClaimAllowed: false,
    changeNotes: "Do not turn RAQOZ into an anti-bribery management system.",
  },
  {
    frameworkId: "iso-22000-2018-amd1-2024",
    frameworkName: "Food safety management systems",
    issuer: "International Organization for Standardization",
    jurisdiction: "Global",
    version: "ISO 22000:2018 with Amendment 1:2024",
    status: "under-revision",
    publicationDate: "2018-06",
    effectiveDate: null,
    transitionPeriod: "A replacement edition is under development; verify status before any public mapping.",
    appliesTo: "Organisations in the food chain using or seeking certification to the standard.",
    thresholds: "No automatic applicability.",
    applicability: ["voluntary-certification", "contractual-requirement"],
    procurementRelevance: "Approved suppliers, incoming material and packaging evidence require specialist food-safety validation.",
    evidenceCommonlyRequested: ["Supplier approval", "Material specifications", "Incoming evidence", "Nonconformity and traceability"],
    raqozSupport: ["Supplier onboarding", "Purchase requirements", "Receipt and discrepancy evidence"],
    missingCapability: ["Food-safety practitioner mapping", "Lot-level regulatory validation"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "watch",
    searchDemand: "baseline-pending",
    channelOpportunity: "Food manufacturing only after validation",
    primarySource: "https://www.iso.org/standard/65464.html",
    secondarySources: ["https://www.iso.org/standard/88794.html"],
    lastVerified: "2026-09-01",
    nextReview: "2026-12-01",
    dependentRoutes: [],
    dependentProductMappings: ["supplier-master", "receipt", "consumable"],
    publicClaimAllowed: false,
    changeNotes: "Watchlist; no public framework page authorised.",
  },
  {
    frameworkId: "eu-dora-2022-2554",
    frameworkName: "Digital Operational Resilience Act",
    issuer: "European Union",
    jurisdiction: "European Union",
    version: "Regulation (EU) 2022/2554",
    status: "watchlist",
    publicationDate: "2022-12-27",
    effectiveDate: "2025-01-17",
    transitionPeriod: "Applicable scope and proportionality require specialist review.",
    appliesTo: "Defined financial entities and relevant ICT third-party contexts, with exclusions and proportionality rules.",
    thresholds: "RAQOZ does not determine whether an entity or service is in scope.",
    applicability: ["direct-law"],
    procurementRelevance: "ICT third-party due diligence, contracts and register evidence; outside RAQOZ's immediate SME wedge.",
    evidenceCommonlyRequested: ["ICT supplier register", "Due diligence", "Contract evidence", "Risk and review records"],
    raqozSupport: ["Supplier and contract evidence only"],
    missingCapability: ["DORA register/reporting", "ICT risk management", "Regulatory mapping"],
    professionalReview: "professional-review-required",
    claimRisk: "high",
    commercialUrgency: "watch",
    searchDemand: "baseline-pending",
    channelOpportunity: "Not an immediate target",
    primarySource: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32022R2554",
    secondarySources: [],
    lastVerified: "2026-09-01",
    nextReview: "2027-03-01",
    dependentRoutes: [],
    dependentProductMappings: [],
    publicClaimAllowed: false,
    changeNotes: "Watchlist only; no page or product feature authorised.",
  },
];

export function frameworkById(frameworkId: string) {
  return frameworkRegistry.find((entry) => entry.frameworkId === frameworkId);
}

export function frameworksForRoute(path: string) {
  return frameworkRegistry.filter((entry) => entry.dependentRoutes.includes(path));
}
